import { after_save_filter_custom_predecessor } from '../../../assets/js/custom_gantt_fields/custom_predecessor'
import { after_save_filter_custom_sucessor } from '../../../assets/js/custom_gantt_fields/custom_sucessor';
import EventEmitter from 'react-native-eventemitter';
import { trackingEvent } from '../../../analytics';
import { getBasicAmplitudEventProperties } from '../../../analytics/utils';
import { AMPLITUDE_SERVICE } from '../../../analytics/constants';
import cloneDeep from 'lodash/cloneDeep';
import { store } from '../../../redux/store';

const shouldTrackTaskMove = (() => {
  const hasConstraintValidation = Boolean(localStorage.getItem('constraintValidation'));
  return () => hasConstraintValidation;
})();

const AUTOSCHEDULE_COLUMNS = {
  hhWorkTime: true,
  progress: true,
  text: true,
  cost: true,
  used_cost: true
};

const DATE_COLUMNS = {
  end_date: true,
  start_date: true,
  duration: true,
  constraint_date: true,
  calendar_id: true
};

const DATES_INPUTS = {
  start_date: true,
  end_date: true,
  constraint_date: true
};

const CONSTRAINT_TYPES = {
  MFO: 'mfo',
  MSO: 'mso',
  FNLT: 'fnlt',
  FNET: 'fnet',
  SNET: 'snet',
  SNLT: 'snlt'
};

const eventMappings = {
  text: 'activity_name_entry',
  description: 'activity_description_entry',
  hhWorkTime: 'activity_labor_hours_budgeted_entry',
  cost: 'activity_cost_budgeted_entry',
  used_cost: 'activity_cost_spent_entry'
};

const fieldsToValidateDate = ['start_date', 'end_date'];

const getCustomHourValue = () => {
  return store.getState().calendarCustomHourState.customHour;
};

// Helper function to reduce code duplication
const notifyConstraint = (gantt, task, extraParam = false) => {
  gantt.notificationConstraint(
    task.constraint_date,
    task.constraint_type,
    task.id,
    extraParam
  );
};

// Optimized date comparison
const isSameDay = (date1, date2) => {
  return Math.floor(date1.getTime() / 86400000) === Math.floor(date2.getTime() / 86400000);
};

// Constraint validation rules
const constraintValidations = {
  [CONSTRAINT_TYPES.MSO]: () => true,
  [CONSTRAINT_TYPES.MFO]: () => true,
  [CONSTRAINT_TYPES.SNLT]: (task, value) => task.constraint_date < value,
  [CONSTRAINT_TYPES.SNET]: (task, value) => task.constraint_date > value,
  [CONSTRAINT_TYPES.FNLT]: (task, value) => task.constraint_date < value,
  [CONSTRAINT_TYPES.FNET]: (task, value) => task.constraint_date > value
};

function onBeforeSave(state) {
  const gantt = window.to_use_react_gantt;
  let { id, columnName, oldValue, newValue } = state;
  
  if (DATES_INPUTS[columnName] && gantt.isPasting) {
    if (
      gantt.validateFormatDay &&
      !gantt.validateFormatDay(gantt.stringDateInput)
    ) {
      return false;
    }
  }


  const task = gantt.getTask(id);
  
  if(!task) return;

  after_save_filter_custom_predecessor(gantt)(state, (arrActivities) => {
    gantt.allLinksBefore = gantt.getLinks();
    EventEmitter.emit('openCircularLinksModal', arrActivities);
  });

  after_save_filter_custom_sucessor(gantt)(state, (arrActivities) => {
    gantt.allLinksBefore = gantt.getLinks();
    EventEmitter.emit('openCircularLinksModal', arrActivities);
  });

  gantt.lastUpdatedColumn = columnName;
  /** Avoid inputs to autoschedule */
  if (AUTOSCHEDULE_COLUMNS[columnName]) {
    gantt.avoidAutoschedule = true;
  }

  if (eventMappings[columnName] !== undefined) {
    const eventName = eventMappings[columnName];
    trackingEvent(
      eventName,
      {
        ...getBasicAmplitudEventProperties(),
        activity_name: task?.text,
        activity_id: task?.id,
        activity_index: task?.correlative_id
      },
      AMPLITUDE_SERVICE
    );
  }

  if (columnName == 'calendar_id') {
    /** We set this value to false, because is the user which assign this calendar */
    if (newValue == '') {
      return false;
    }
    if (task.calendar_id == newValue) {
      return false;
    }
    task.assignedDefaultCalendar = false;
    task.calendar_id = newValue;
    trackingEvent(
      'activity_calendar_assignment',
      {
        ...getBasicAmplitudEventProperties(),
        activity_name: task?.text,
        activity_id: task?.id,
        activity_index: task?.correlative_id
      },
      AMPLITUDE_SERVICE
    );
  } else if (columnName == 'progress') {
    const n = parseInt(state.newValue);
    if (n > 100 || n < 0) {
      return false;
    } else if (n == 0) {
      task.auto_scheduling = true;
    } else if (n > 0) {
      task.auto_scheduling = false;
      trackingEvent(
        'activity_progress_entry',
        {
          ...getBasicAmplitudEventProperties(),
          activity_name: task?.text,
          activity_id: task?.id,
          activity_index: task?.correlative_id
        },
        AMPLITUDE_SERVICE
      );
    }
  } else if (columnName == 'custom_predecessors') {
    const inputElement = document.getElementsByName(state.columnName);
    if (inputElement.length) {
      const element = inputElement[0];
      element.autocomplete = 'off';
      element.value = task.custom_predecessors;
    }
    task.old_custom_predecessor = task.custom_predecessors;
  } else if (columnName == 'duration') {
    const hasNegativeValue = Math.sign(newValue) === -1;
    if (hasNegativeValue) return false;

    const calendar = gantt.getCalendar(task.calendar_id);
    const new_date_end = calendar.calculateEndDate(
      task.start_date,
      newValue
    );
    if ([CONSTRAINT_TYPES.MFO].includes(task.constraint_type)) {
      return gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        false
      );
    }
    if (
      [CONSTRAINT_TYPES.FNLT].includes(task.constraint_type) &&
      task.constraint_date < new_date_end
    ) {
      return gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        false
      );
    }
    if (
      [CONSTRAINT_TYPES.FNET].includes(task.constraint_type) &&
      task.constraint_date > new_date_end
    ) {
      return gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        false
      );
    }
    if (!newValue) {
      newValue = 0;
      task.is_milestone = true;
    } else {
      task.is_milestone = false;
    }

    if (oldValue == 0 && newValue > 0) {
      task.type = 'task';
    } else if (oldValue === newValue) {
      return false;
    } else {
      task.bug_duration_from_resize_drag = true;
      task.inline_duration_value = newValue;
      trackingEvent(
        'activity_duration_entry',
        {
          ...getBasicAmplitudEventProperties(),
          activity_name: task?.text,
          activity_id: task?.id,
          activity_index: task?.correlative_id
        },
        AMPLITUDE_SERVICE
      );
    }
  } else if (columnName == 'constraint_type') {
    const constraint_type_old = task.constraint_type;
    const constraint_date_old = task.constraint_date;
    task.constraint_type = newValue;
    /** This flags allow after update task to correct with correct constraint type */
    /** This flag saves real constraint type */
    task.real_constraint_type = newValue;

    /** variables used for cases of constraint types */
    task.constraint_type_old = constraint_type_old;
    task.constraint_date_old = constraint_date_old;

    gantt.constraint_bug = true;
  } else if (columnName == 'constraint_date') {
    const calendar = gantt.getCalendar(task.calendar_id);

    // Funcion mal utilizada deberia ser {date, unit: 'day'}
    if (
      !calendar.isWorkTime({
        date: gantt.date.parseDate(newValue, 'xml_date'),
        unit: 'day'
      })
    ) {
      return false;
    }
    task.constraint_type_last = task.constraint_type;

    if (
      ['asap', 'alap'].includes(task.constraint_type) ||
      !task.constraint_type
    ) {
      task.constraint_type = 'snet';
    }

    task.last_constraint_date = task.constraint_date;
    const clone_constraint_date = calendar.getClosestWorkTime({
      dir: 'fut', // fut past
      date: newValue,
      unit: gantt.config.duration_unit
    });
    task.constraint_date = clone_constraint_date;
    task.real_constraint_type = task.constraint_type;
    task.constraint_type_old = task.constraint_type;

    gantt.constraint_bug = true;
  } else if (columnName == 'hhWorkTime') {
    task.hhWorkTime = newValue;
  } else if (columnName == 'start_date') {
    const dateFormat = gantt.currentDateFormat;
    const calendar = gantt.getCalendar(task.calendar_id);
    const new_value_hours = calendar.getClosestWorkTime({
      dir: 'fut',
      date: newValue,
      unit: gantt.config.duration_unit
    });
    
    if (shouldTrackTaskMove()) {
      window.gantt.lastMoveTaskData = [
        {
          id: task.id,
          data: cloneDeep(task)
        }
      ];
    }

    const clone_new_value =
      dateFormat.split(' ').length > 1 ? newValue : new_value_hours;

    const new_date_end = calendar.calculateEndDate(
      clone_new_value,
      task.duration
    );

    // Check all constraint validations in one go
    const validator = constraintValidations[task.constraint_type];
    if (validator && validator(task, new_date_end)) {
      notifyConstraint(gantt, task);
    }

    if (isSameDay(task.start_date, clone_new_value)) {
      return false;
    }
    task.constraint_date = clone_new_value;
    task.constraint_type = 'snet';

    task.last_constraint_date = clone_new_value;
    task.real_constraint_type = 'snet';
    task.constraint_type_old = 'snet';

    task.start_date = clone_new_value;

    if (task.constraint_type != 'alap' && task.last_constraint != 'alap') {
      gantt.modifyLagCustom(task);
    }
    trackingEvent(
      'activity_start_date_entry',
      {
        ...getBasicAmplitudEventProperties(),
        activity_name: task?.text,
        activity_id: task?.id,
        activity_index: task?.correlative_id
      },
      AMPLITUDE_SERVICE
    );
  } else if (columnName == 'end_date') {
    const dateFormat = gantt.currentDateFormat;
    const calendar = gantt.getCalendar(task.calendar_id);
    const new_value_hours = calendar.getClosestWorkTime({
      dir: 'past',
      date: new Date(newValue.getTime() + 24 * 60 * 60 * 1000),
      unit: gantt.config.duration_unit
    });

    const clone_new_value =
      dateFormat.split(' ').length > 1 ? newValue : new_value_hours;

    if (shouldTrackTaskMove()) {
      window.gantt.lastMoveTaskData = [
        {
          id: task.id,
          data: cloneDeep(task)
        }
      ];
    }

    if (['mfo'].includes(task.constraint_type))
      gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        task.id,
        false
      );
    if (
      ['fnlt'].includes(task.constraint_type) &&
      task.constraint_date < clone_new_value
    )
      gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        task.id,
        false
      );
    if (
      ['fnet'].includes(task.constraint_type) &&
      task.constraint_date > clone_new_value
    )
      gantt.notificationConstraint(
        task.constraint_date,
        task.constraint_type,
        task.id,
        false
      );

    const dateStr = clone_new_value;

    if (Object.keys(getCustomHourValue()).length !== 0) {
      const hours = getCustomHourValue().endHour.split(':')[0];
      const minutes = getCustomHourValue().endHour.split(':')[1];
      dateStr.setHours(hours);
      dateStr.setMinutes(minutes);
    }

    const copyOfdateStr = cloneDeep(dateStr);

    if (isSameDay(task.end_date, dateStr)) {
      return false;
    } else if (dateStr < task.date_origin) {
      return false;
    } else if (dateStr < task.start_date) {
      return false;
      // Funcion mal utilizada deberia ser {date, unit: 'day'}
    } else if (!calendar.isWorkTime({ date: copyOfdateStr, unit: 'day' })) {
      return false;
    }
    if (['asap', 'alap'].includes(task.constraint_type)) {
      task.constraint_date = task.start_date;
      task.constraint_type = 'snet';

      task.last_constraint_date = task.start_date;
      task.real_constraint_type = 'snet';
      task.constraint_type_old = 'snet';
    }
    const newDuration = calendar.calculateDuration(
      new Date(task.start_date),
      new Date(dateStr)
    );

    /** ctid#13 mp-252 */
    task.duration_new = newDuration;
    task.for_disable_milestone_duration = newDuration;
    task.end_date_new = dateStr;

    trackingEvent(
      'activity_end_date_entry',
      {
        ...getBasicAmplitudEventProperties(),
        activity_name: task?.text,
        activity_id: task?.id,
        activity_index: task?.correlative_id
      },
      AMPLITUDE_SERVICE
    );
  }
  if (DATE_COLUMNS[columnName]) {
    setTimeout(() => {
      gantt.autoSchedule();
    }, 500);
  }

  return true;
}

export { onBeforeSave };